import { Controller } from "@hotwired/stimulus"

// Two-step catalog picker for quote offerings: choose the main product, then
// its pricing option. The product select drives the price select — changing
// the product repopulates the pricing options from a preloaded map and fires a
// change so the surrounding quote form recalculates.
//
// Markup:
//   <div data-controller="catalog-select"
//        data-catalog-select-options-value='{"<productExtId>":[["Monthly","catalog:<priceExtId>"], ...]}'>
//     <select data-catalog-select-target="product" data-action="change->catalog-select#productChanged">…</select>
//     <select data-catalog-select-target="price" data-action="change->sales-quote-form#recalc">…</select>
//   </div>
export default class extends Controller {
  static targets = ["product", "price", "description"]
  // options: { productExtId: [[label, "catalog:priceExt"], …] }
  // prices:  { "catalog:priceExt": { total: "$30/Monthly", billing: "Monthly" } }
  // descriptions: { productExtId: "<p>master HTML</p>" }
  static values = { options: Object, prices: Object, descriptions: Object }

  connect() {
    // Server rendered the price select for the initial product; just make sure
    // its visibility matches the option count (hidden when there's no real choice).
    if (this.hasPriceTarget) this.togglePriceVisibility(this.currentOptions().length)
  }

  productChanged() {
    this.fillDescription()
    if (!this.hasPriceTarget) return

    this.renderPriceOptions()
    // Let the quote form recalc/save off the freshly selected pricing option.
    // This change also runs offeringChanged (below) to clear any fee override.
    this.priceTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // The description target is a trix-editor; .value writes Trix HTML.
  fillDescription() {
    if (!this.hasDescriptionTarget) return
    this.descriptionTarget.value = this.descriptionsValue[this.productTarget.value] || ""
  }

  // A different product/pricing option means a different catalog price. On an
  // editable-fee line (e.g. Software, Tax) a stale manual fee would otherwise
  // override it, so clear the fee input and let the new catalog price apply —
  // the rep can re-enter a custom fee afterward. On a catalog-priced (Software)
  // row, also refresh the Total/Billing cells live so the rep sees the new price
  // before saving.
  offeringChanged() {
    const fee = this.element.closest("form")?.querySelector('[name="sales_quote[line_attributes][fee]"]')
    if (fee) fee.value = ""
    this.refreshCatalogTotal()
  }

  refreshCatalogTotal() {
    const form = this.element.closest("form")
    if (!form) return
    const display = this.pricesValue[this.priceTarget.value] || { total: "$0/—", billing: "—" }
    const totalEl = form.querySelector("[data-catalog-total]")
    const billingEl = form.querySelector("[data-catalog-billing]")
    if (totalEl) totalEl.textContent = display.total
    if (billingEl) billingEl.textContent = display.billing
  }

  currentOptions() {
    return this.optionsValue[this.productTarget.value] || []
  }

  renderPriceOptions() {
    const options = this.currentOptions()
    this.priceTarget.innerHTML = ""

    if (options.length === 0) {
      this.priceTarget.appendChild(this.buildOption("", "—"))
      this.priceTarget.disabled = true
    } else {
      this.priceTarget.disabled = false
      options.forEach(([label, value], i) => {
        const option = this.buildOption(value, label)
        if (i === 0) option.selected = true
        this.priceTarget.appendChild(option)
      })
    }

    this.togglePriceVisibility(options.length)
  }

  // Only show the pricing-option select when there's more than one option;
  // otherwise it just repeats the product. Kept in the DOM either way so its
  // value still submits.
  togglePriceVisibility(optionCount) {
    this.priceTarget.classList.toggle("d-none", optionCount <= 1)
    this.productTarget.classList.toggle("mb-1", optionCount > 1)
  }

  buildOption(value, label) {
    const option = document.createElement("option")
    option.value = value
    option.textContent = label
    return option
  }
}
